import { useMediaQuery } from '@mui/material';
import { format } from 'date-fns';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../../App.styles';
import { useThemeContext } from '../../../../../../global-state/themeContext';
import { StockAccuracyItem } from '../../../../../../hooks';
import { Container, Icon, Icons, Typography } from '../../../../../../stories/atoms';
import { Button, DynamicTab, Modal } from '../../../../../../stories/molecules';
import { muiTheme, Typographies } from '../../../../../../theme';
import { commonTranslations, homeTranslations } from '../../../../../../translations';
import { getAggregateStockAccuracyData } from '../util';
import { AccuracyChart } from './AccuracyChart';
import { IconContainer, StyledModalWrapper } from './StockAccuracyModal.styles';

export type StockAccuracyModalProps = {
  stockAccuracyData: StockAccuracyItem[];
  isOpen: boolean;
  onClose: () => void;
};

export const StockAccuracyModal: FC<StockAccuracyModalProps> = ({ stockAccuracyData, isOpen, onClose }) => {
  const { t } = useTranslation();
  const { stockAccuracy: stockAccuracyTranslations, rfidApprovals: rfidApprovalsTranslations } = homeTranslations;
  const { close } = commonTranslations;
  const { mode } = useThemeContext();
  const [tabIndex, setTabIndex] = useState(0);

  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const sortedStockAccuracyData = useMemo(() => {
    return stockAccuracyData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [stockAccuracyData]);

  const dateRanges = useMemo(() => {
    return sortedStockAccuracyData.map((item) => format(new Date(item.date), 'EEEEE').toString());
  }, [sortedStockAccuracyData]);

  const stockAccuracyAggregate = useMemo(() => {
    return sortedStockAccuracyData.map((item) => getAggregateStockAccuracyData([item]));
  }, [sortedStockAccuracyData]);

  const getChartSeries = () => {
    if (tabIndex === 0) {
      return [
        {
          name: t(stockAccuracyTranslations.accuracy.key, stockAccuracyTranslations.accuracy.defaultValue),
          data: stockAccuracyAggregate.map((item) => item.stockAccuracyPercentage),
        },
      ];
    }
    if (tabIndex === 1) {
      return [
        {
          name: t(
            rfidApprovalsTranslations.positiveTitle.key,
            rfidApprovalsTranslations.positiveTitle.defaultValue,
          ),
          data: stockAccuracyAggregate.map((item) => item.positiveDifference),
        },
      ];
    }

    return [
      {
        name: t(rfidApprovalsTranslations.negativeTitle.key, rfidApprovalsTranslations.negativeTitle.defaultValue),
        data: stockAccuracyAggregate.map((item) => item.negativeDifference),
      },
    ];
  };

  const handleTabChange = (event: React.SyntheticEvent, newTabIndex: number) => {
    setTabIndex(newTabIndex);
  };

  return (
    <Modal isOpen={isOpen} handleClose={onClose}>
      <StyledModalWrapper
        data-testid="stock-accuracy-modal-container"
        wrap="nowrap"
        isMobile={isMobile}
        width={isTablet ? '' : '800px'}
      >
        <Container wrap="nowrap" direction="horizontal" style={{ alignItems: 'center' }}>
          <IconContainer isMobile={isMobile}>
            <Icon icon={Icons.DELIVERY} size="large" />
          </IconContainer>

          <Container wrap="nowrap">
            <Typography type="h4">
              {t(stockAccuracyTranslations.title.key, stockAccuracyTranslations.title.defaultValue)}
            </Typography>
            <SecondaryTextTypography type="body3">
              {t(
                stockAccuracyTranslations.weeklyViewText.key,
                stockAccuracyTranslations.weeklyViewText.defaultValue,
              )}
            </SecondaryTextTypography>
          </Container>
        </Container>
        <DynamicTab
          tabLabelArray={[
            {
              tabName: t(stockAccuracyTranslations.accuracy.key, stockAccuracyTranslations.accuracy.defaultValue),
              isTabVisible: true,
              isTabDisabled: false,
              labelType: 'button' as Typographies,
            },
            {
              tabName: t(
                rfidApprovalsTranslations.positiveTitle.key,
                rfidApprovalsTranslations.positiveTitle.defaultValue,
              ),
              isTabVisible: true,
              isTabDisabled: false,
              labelType: 'button' as Typographies,
            },
            {
              tabName: t(
                rfidApprovalsTranslations.negativeTitle.key,
                rfidApprovalsTranslations.negativeTitle.defaultValue,
              ),
              isTabVisible: true,
              isTabDisabled: false,
              labelType: 'button' as Typographies,
            },
          ]}
          handleTabChange={handleTabChange}
          tabValue={tabIndex}
          variant="scrollable"
          tabBgColor={mode === 'light' ? 'white' : 'backgroundPaperDark'}
          content={[]}
        />

        <AccuracyChart chartSeries={getChartSeries()} dateRanges={dateRanges} />
        <Button
          data-testid="close-button"
          variant="secondary"
          label={t(close.key, close.defaultValue)}
          fullWidth
          onClick={onClose}
        />
      </StyledModalWrapper>
    </Modal>
  );
};
